import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { ShellRail, useShellUI } from '../../shell/AppShell';
import MarketNav from './MarketNav';
import { fetchLeaderboard, setLeaderboardOptIn, fmtPct } from './marketApi';
import './market.css';

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

const MarketLeaderboard = () => {
  const navigate = useNavigate();
  const { toast } = useShellUI();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [deltaFlash, setDeltaFlash] = useState({});
  const [justOptedIn, setJustOptedIn] = useState(false);
  const rowRefs = useRef(new Map());
  const prevTops = useRef(new Map());
  const prevRanks = useRef(new Map());

  const load = useCallback(
    () => fetchLeaderboard().then(setData).catch((e) => setError(e.message)),
    []
  );

  useEffect(() => {
    load();
    const poll = setInterval(load, 60000);
    return () => clearInterval(poll);
  }, [load]);

  // FLIP: rows reorder in place on a rank change instead of jump-cutting.
  useLayoutEffect(() => {
    if (!data) return;
    const reduced = prefersReducedMotion();
    const nextTops = new Map();
    rowRefs.current.forEach((el, accountId) => {
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      nextTops.set(accountId, top);
      const prevTop = prevTops.current.get(accountId);
      if (prevTop !== undefined && !reduced) {
        const delta = prevTop - top;
        if (Math.abs(delta) > 0.5) {
          el.style.transition = 'none';
          el.style.transform = `translateY(${delta}px)`;
          // eslint-disable-next-line no-unused-expressions
          el.offsetHeight; // force reflow before releasing into the transition
          el.style.transition = '';
          el.style.transform = '';
        }
      }
    });
    prevTops.current = nextTops;
  }, [data]);

  // Rank-delta chip — "+N" when a row climbs since the last poll.
  useEffect(() => {
    if (!data) return;
    const next = {};
    data.rows.forEach((r) => {
      const prevRank = prevRanks.current.get(r.accountId);
      if (prevRank !== undefined && prevRank !== r.rank) {
        next[r.accountId] = prevRank - r.rank; // positive = climbed
      }
      prevRanks.current.set(r.accountId, r.rank);
    });
    if (Object.keys(next).length) {
      setDeltaFlash(next);
      const t = setTimeout(() => setDeltaFlash({}), 1800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [data]);

  const optIn = (v) => () =>
    setLeaderboardOptIn(v)
      .then(() => {
        if (v) {
          setJustOptedIn(true);
          setTimeout(() => setJustOptedIn(false), 4200);
        } else {
          toast('Off the board');
        }
        load();
      })
      .catch((e) => toast(e.message));

  if (error) {
    return (
      <div className="ft-view">
        <MarketNav />
        <p className="ft-sub">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="ft-view">
        <MarketNav />
        <div className="ft-loading">Ranking the desks</div>
      </div>
    );
  }

  return (
    <div className="ft-view">
      <Helmet>
        <title>Leaderboard — The Market — Fantasy Taco</title>
      </Helmet>
      <MarketNav />

      <p className="rh-label">
        Leaderboard
        <span>private by default, public if you add yourself</span>
      </p>
      <div style={{ height: 18 }} />

      {!data.myOptedIn && (
        <div className="ft-card" style={{ padding: '18px 20px', marginBottom: 18 }}>
          <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 4px' }}>You're not listed.</p>
          <p style={{ fontSize: 13, color: 'var(--ft-ink-2)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Your portfolio stays sealed until you add it — name and return since opening only,
            nothing else.
          </p>
          <button type="button" className="ft-btn sm" onClick={optIn(true)}>
            Add me to the leaderboard
          </button>
        </div>
      )}

      {justOptedIn && (
        <div className="ft-card mk-ceremony">
          You're on the board. <b>Private, until you say otherwise.</b>
        </div>
      )}

      {data.rows.length === 0 && (
        <div className="ft-card" style={{ padding: '24px 22px' }}>
          <p style={{ fontSize: 15, fontWeight: 600, margin: '0 0 6px' }}>No desks on the board yet.</p>
          <p style={{ fontSize: 13.5, color: 'var(--ft-ink-2)', margin: '0 0 14px' }}>
            The first trade opens the standings.
          </p>
          <button type="button" className="ft-btn sm" onClick={() => navigate('/market/browse')}>
            Make the first move
          </button>
        </div>
      )}

      {data.rows.map((r) => (
        <div
          className={`ft-card mk-rank${r.accountId === data.myAccountId ? ' you' : ''}${r.rank === 1 ? ' is-first' : ''}`}
          key={r.accountId}
          ref={(el) => {
            if (el) rowRefs.current.set(r.accountId, el);
            else rowRefs.current.delete(r.accountId);
          }}
        >
          <span className="pos mk-mono">{r.rank}</span>
          <span className="nm">
            {r.accountId === data.myAccountId ? 'You' : r.name}
            <span>
              {r.positions} {r.positions === 1 ? 'position' : 'positions'}
            </span>
          </span>
          {deltaFlash[r.accountId] !== undefined && (
            <span className={`delta show${deltaFlash[r.accountId] < 0 ? ' down' : ''}`}>
              {deltaFlash[r.accountId] > 0 ? '+' : ''}
              {deltaFlash[r.accountId]}
            </span>
          )}
          <span className={`ret mk-mono ${r.returnPct >= 0 ? 'mk-up' : 'mk-dn'}`}>{fmtPct(r.returnPct, 2)}</span>
        </div>
      ))}

      {data.myRank && data.myRank > data.rows.length && (
        <p style={{ fontSize: 13, color: 'var(--ft-ink-3)', marginTop: 14 }}>
          You hold rank {data.myRank}. The board shows the top {data.rows.length}.
        </p>
      )}

      <ShellRail>
        <div className="ft-railsec">
          <span className="ft-k">How ranking works</span>
          <p style={{ fontSize: 13, color: 'var(--ft-ink-2)', margin: 0, lineHeight: 1.5 }}>
            Everyone starts with the same stack. Return since opening is the only number. Holdings
            stay sealed — only name and return show, and only for those who add themselves.
          </p>
        </div>
        <div className="ft-railsec">
          <span className="ft-k">Privacy</span>
          <p style={{ fontSize: 13, color: 'var(--ft-ink-2)', margin: '0 0 10px', lineHeight: 1.5 }}>
            Off the board by default. Add yourself when you're ready to be seen, and pull yourself
            off again any time.
          </p>
          {data.myOptedIn && (
            <button type="button" className="ft-btn sm" onClick={optIn(false)}>
              Remove me from the leaderboard
            </button>
          )}
        </div>
      </ShellRail>
    </div>
  );
};

export default MarketLeaderboard;
